import { CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { Subject, takeUntil } from 'rxjs';
import { SwalToastService } from 'src/app/services/swal.service';
import { IAddGlasgow } from 'src/app/interfaces/Glasgow/IAddGlasgow';
import { IGlasgowDropdown } from 'src/app/interfaces/Glasgow/IGlasgowDropdown';
import { GlasgowService } from 'src/app/services/glasgow.service';
import { dateRangeValidator } from 'src/date/date.configuration';

@Component({
  selector: 'app-glasgow-add-edit',
  standalone: true,
  templateUrl: './glasgow-add-edit.component.html',
  styleUrls: ['./glasgow-add-edit.component.css'],
  imports: [
    NzModalModule,
    TranslateModule,
    FormsModule,
    FlexLayoutModule,
    ReactiveFormsModule,
    NzSelectModule,
    CommonModule,
    NzButtonModule,
    NzTableModule,
    NzLayoutModule,
    NzIconModule,
  ],
})
export class GlasgowAddEditComponent {
  private destroy$ = new Subject<void>();

  isVisible = false;
  isLoading = false;
  isDropdownLoading = false;

  isEdit = false;
  editGlasgowId?: number;

  glasgowSubmitted = false;

  dropdown: IGlasgowDropdown = {};

  @ViewChild('modalRef', { static: true }) modalRef!: NzModalRef; // Reference to the modal

  glasgowForm: FormGroup = new FormGroup({});

  constructor(
    private readonly glasgowService: GlasgowService,
    private readonly formBuilder: FormBuilder,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService
  ) {}

  ngOnInit() {
    this.isEdit = false;
    this.isLoading = true;
    this.isDropdownLoading = true;

    this.glasgowForm = this.formBuilder.group({
      dateValue: [
        null,
        [Validators.required, dateRangeValidator(undefined, new Date())],
      ],
      eyeOpeningValue: [null, Validators.required],
      verbalResponseValue: [null, Validators.required],
      motorResponseValue: [null, Validators.required],
    });

    this.glasgowService.getGlasgowDropdown().subscribe({
      next: (pro) => {
        this.isDropdownLoading = false;
        this.dropdown = pro?.data;
      },
      error: (err) => {},
    });

    this.glasgowService.glasgowEditSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        // GET DATA FIELDS AND ASSIGN DEFAULT VALUES TO FORM
        if (data) {
          this.isEdit = true;
          this.isLoading = false;

          this.editGlasgowId = data?.id;

          this.glasgowForm.patchValue({
            dateValue:
              data?.onSetDateTime && this.convertToLocale(data?.onSetDateTime),
            eyeOpeningValue: data?.eyeOpeningId,
            verbalResponseValue: data?.verbalResponseId,
            motorResponseValue: data?.motorResponseId,
          });
        } else {
          this.isEdit = false;
          this.isLoading = false;

          this.editGlasgowId = undefined;

          this?.glasgowForm.reset();

          this.glasgowForm.patchValue({
            dateValue: this.convertToLocale(),
          });
        }
      });

    this.glasgowService.glasgowModalSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((showModal) => {
        this.isVisible = showModal;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    this?.modalRef.destroy();
  }

  handleCancel() {
    this.glasgowForm.reset();

    this.glasgowService.glasgowModalSub.next(false);

    this.glasgowSubmitted = false;

    this.isLoading = false;

    this.modalRef.destroy();
  }

  submitForm() {
    this.isLoading = true;
    this.glasgowSubmitted = true;

    if (this.glasgowForm.valid) {
      const glasgowObj: IAddGlasgow = {};

      glasgowObj.id = this.editGlasgowId;

      glasgowObj.onSetDateTime = moment(
        this.glasgowForm.value?.dateValue
      ).format();

      glasgowObj.eyeOpeningId = this.glasgowForm.get('eyeOpeningValue')?.value;
      glasgowObj.verbalResponseId = this.glasgowForm.get(
        'verbalResponseValue'
      )?.value;
      glasgowObj.motorResponseId =
        this.glasgowForm.get('motorResponseValue')?.value;

      if (this.isEdit) {
        this.glasgowService.editGlasgow(glasgowObj).subscribe({
          next: () => {
            this.swalToastService.toastSuccess(
              'Glasgow record edited successfully'
            );
            this.glasgowService.getData();

            this.handleCancel();
          },
          error: (err) => {
            this.swalToastService.toastError('Unable to edit Glasgow record');

            this.handleCancel();
          },
        });
      } else {
        this.glasgowService.addGlasgow(glasgowObj).subscribe({
          next: () => {
            this.swalToastService.toastSuccess(
              'Glasgow record added successfully'
            );
            this.glasgowService.getData();

            this.handleCancel();
          },
          error: (err) => {
            this.swalToastService.toastError('Unable to add Glasgow record');
          },
        });
      }
    } else {
      this.isLoading = false;
    }
  }

  getCurrentDateTime() {
    return new Date().toISOString().slice(0, 16);
  }

  // CONVERT UTC TO LOCAL
  convertToLocale(date?: string) {
    const currentDateTime = date ? moment(date).format() : moment().format();

    const isoDate = currentDateTime.slice(0, 16);

    return isoDate;
  }
}
